
import type { Dispatch } from 'redux';

import {
    createStartMutedConfigurationEvent,
    sendAnalytics
} from '../../analytics';
import { abortRoleTransition, setIAmVisitor, setRoleTransition } from '../../visitors/actions';
import { verr, vlog } from '../../visitors/debug';
import { normalizePromotionRole } from '../../visitors/functions';
import { SET_START_BACKSTAGE } from '../../webinar/actionTypes';
import { BACKSTAGE_ROOM_SUFFIX, WEBINAR_HIDDEN_REQUEST_PROPERTY } from '../../webinar/constants';
import { getJoinHiddenAudience, shouldDirectJoinBackstage } from '../../webinar/functions';
import { overwriteConfig } from '../config';
import { disconnectAny, setPreferVisitor } from '../connection/actions.any';
import { MH_CONNECTION_CONFERENCE_KEY } from '../connection/constants';
import { hasAvailableDevices } from '../devices/functions';
import {
    MHConferenceEvents,
    MHConnectionQualityEvents
} from '../lib-meet-hour';
import { gumPending, setAudioMuted, setVideoMuted, setVideoUnmutePermissions } from '../media/actions';
import { MEDIA_TYPE, VIDEO_MUTISM_AUTHORITY } from '../media/constants';
import { IGUMPendingState } from '../media/types';
import {
    dominantSpeakerChanged,
    participantConnectionStatusChanged,
    participantKicked,
    participantMutedUs,
    participantPresenceChanged,
    participantRoleChanged,
    participantUpdated
} from '../participants/actions';
import { getNormalizedDisplayName } from '../participants/functions';
import { destroyLocalDesktopTrackIfExists, destroyLocalTracks, replaceLocalTrack, trackAdded, trackMutedChanged, trackRemoved } from '../tracks/actions';
import { getLocalTracks } from '../tracks/functions';
import {
    getBackendSafeRoomName
} from '../util';

import {
    ADD_FORCE_MUTE_REMOTE_PARTICIPANT,
    AUTH_STATUS_CHANGED,
    CALL_ENDED,
    CONFERENCE_EXPIRED,
    CONFERENCE_FAILED,
    CONFERENCE_JOINED,
    CONFERENCE_JOIN_IN_PROGRESS,
    CONFERENCE_LEFT,
    CONFERENCE_SUBJECT_CHANGED,
    CONFERENCE_TIME,
    CONFERENCE_TIMESTAMP_CHANGED,
    CONFERENCE_UNIQUE_ID_SET,
    CONFERENCE_WILL_INIT,
    CONFERENCE_WILL_JOIN,
    CONFERENCE_WILL_LEAVE,
    CONNECTION_INDICATOR_STATUS,
    DATA_CHANNEL_OPENED,
    ENDPOINT_MESSAGE_RECEIVED,
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_ID,
    FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
    GET_FORCE_MUTE_REMOTE_PARTICIPANT,
    GET_FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
    IS_CUSTOM_PARTICIPANT_EVERYONE,
    IS_CUSTOM_PARTICIPANT_VISIBLE,
    KICKED_OUT,
    LOADING_CHECKER,
    LOCK_STATE_CHANGED,
    MODERATOR_MUTED_US_ALL,
    MODERATOR_UNLOCK_AUDIO,
    MODERATOR_UNLOCK_VIDEO,
    NON_PARTICIPANT_MESSAGE_RECEIVED,
    NOTIFICATION_DETAILS,
    OPEN_NOTIFICATION,
    P2P_STATUS_CHANGED,
    PREJOIN_MOBILE_DISABLED,
    SEND_TONES,
    SET_FOLLOW_ME,
    SET_NAVIGATION_BAR_HIDDEN_ACTIONS,
    SET_OBJECT_FIT,
    SET_PASSWORD,
    SET_PASSWORD_FAILED,
    SET_PENDING_SUBJECT_CHANGE,
    SET_ROOM,
    SET_START_MUTED_POLICY,
    SET_START_REACTIONS_MUTED,
    TOGGLE_LAYOUT_OVERLAY,
    TOGGLE_NAVIGATION_BAR_MORE_OPTIONS,
    TRANSPORT_STATE_CHANGED,
    USER_JOINED_TIME
} from './actionTypes';
import {
    AVATAR_URL_COMMAND,
    DESIGNATION,
    EMAIL_COMMAND,
    MH_CONFERENCE_URL_KEY,
    REDUCE_ALL_USER_VOLUME_TEMPORARILY
} from './constants';
import {
    _addLocalTracksToConference,
    commonUserJoinedHandling,
    commonUserLeftHandling,
    getConferenceOptions,
    getCurrentConference,
    getVisitorOptions,
    sendLocalParticipant
} from './functions';
import logger from './logger';

/**
 * Adds conference (event) listeners.
 *
 * @param {MHConference} conference - The MHConference instance.
 * @param {Dispatch} dispatch - The Redux dispatch function.
 * @param {Object} state - The Redux state.
 * @private
 * @returns {void}
 */
function _addConferenceListeners(conference, dispatch, state, getState) {
    // A simple logger for conference errors received through
    // the listener. These errors are not handled now, but logged.
    conference.on(MHConferenceEvents.CONFERENCE_ERROR,
        error => logger.error('Conference error.', error));

    // Dispatches into features/base/conference follow:

    conference.on(
        MHConferenceEvents.CONFERENCE_FAILED,
        (...args) => {
            // During auto-reconnection, suppress network-related conference
            // failures from the old (dying) conference to prevent UI teardown.
            if (typeof APP !== 'undefined' && APP.conference?.isReconnecting) {
                logger.warn('[AutoReconnect] Suppressing CONFERENCE_FAILED during reconnection:', args[0]);

                return;
            }
            dispatch(conferenceFailed(conference, ...args));
        });
    conference.on(
        MHConferenceEvents.CONFERENCE_JOINED,
        (...args) => {

            try {
                dispatch(conferenceJoined(conference, ...args));
            } catch (error) {
                // This handler runs inside the XMPP/conference library's event
                // emission call stack. If dispatch() throws (e.g. a reducer or
                // middleware error), letting it propagate risks being silently
                // swallowed by the library's own internal try/catch, leaving
                // redux state.conference unset with no visible error.
                logger.error('[DIAG] dispatch(conferenceJoined) threw:', error);
            }
        });
    conference.on(
        MHConferenceEvents.CONFERENCE_LEFT,
        (...args) => {
            // During auto-reconnection, suppress CONFERENCE_LEFT from the old
            // conference to prevent Redux state cleanup and Prejoin redirect.
            if (typeof APP !== 'undefined' && APP.conference?.isReconnecting) {
                logger.warn('[AutoReconnect] Suppressing CONFERENCE_LEFT during reconnection');

                return;
            }

            dispatch(conferenceTimestampChanged(0));
            dispatch(conferenceLeft(conference, ...args));
        });
    conference.on(
        MHConferenceEvents.CONFERENCE_JOIN_IN_PROGRESS,

        () => dispatch(conferenceJoinInProgress(conference)));
    conference.on(MHConferenceEvents.SUBJECT_CHANGED,
        (...args) => dispatch(conferenceSubjectChanged(...args)));

    conference.on(MHConferenceEvents.DONORBOX_URL_RECEIVED,
        (...args) => dispatch(require('../../donorbox/actions').receiveDonorboxURl(...args)));

    conference.on(MHConferenceEvents.CONFERENCE_CREATED_TIMESTAMP,
        (...args) => dispatch(conferenceTimestampChanged(...args)));

    conference.on(
        MHConferenceEvents.KICKED,
        (...args) => dispatch(kickedOut(conference, ...args)));

    conference.on(
        MHConferenceEvents.PARTICIPANT_KICKED,
        (kicker, kicked) => dispatch(participantKicked(kicker, kicked)));

    conference.on(
        MHConferenceEvents.LOCK_STATE_CHANGED,
        (...args) => dispatch(lockStateChanged(conference, ...args)));

    // Dispatches into features/base/media follow:

    conference.on(
        MHConferenceEvents.STARTED_MUTED,
        () => {
            const audioMuted = Boolean(conference.isStartAudioMuted());
            const videoMuted = Boolean(conference.isStartVideoMuted());
            const localTracks = getLocalTracks(state['features/base/tracks']);

            sendAnalytics(createStartMutedConfigurationEvent('remote', audioMuted, videoMuted));
            logger.log(`Start muted: ${audioMuted ? 'audio, ' : ''}${videoMuted ? 'video' : ''}`);

            // XXX Jicofo tells lib-meet-hour to start with audio and/or video
            // muted i.e. Jicofo expresses an intent. lib-meet-hour has turned
            // Jicofo's intent into reality by actually muting the respective
            // tracks. The reality is expressed in base/tracks already so what
            // is left is to express Jicofo's intent in base/media.
            // TODO Maybe the app needs to learn about Jicofo's intent and
            // transfer that intent to lib-meet-hour instead of lib-meet-hour
            // acting on Jicofo's intent without the app's knowledge.
            dispatch(setAudioMuted(audioMuted));
            dispatch(setVideoMuted(videoMuted));

            // Remove the tracks from peerconnection as well.
            for (const track of localTracks) {
                const trackType = track.mhTrack.getType();

                // Do not remove the audio track on RN. Starting with iOS 15 it will fail to unmute otherwise.
                if ((audioMuted && trackType === MEDIA_TYPE.AUDIO && navigator.product !== 'ReactNative')
                    || (videoMuted && trackType === MEDIA_TYPE.VIDEO)) {
                    dispatch(replaceLocalTrack(track.mhTrack, null, conference));
                }
            }
        });

    conference.on(
        MHConferenceEvents.VIDEO_UNMUTE_PERMISSIONS_CHANGED,
        disableVideoMuteChange => {
            dispatch(setVideoUnmutePermissions(disableVideoMuteChange));
        });

    // Dispatches into features/base/tracks follow:

    conference.on(
        MHConferenceEvents.TRACK_ADDED,
        t => {
            logger.log('TRACK ADDED EVENT FIRED', t, t?.getVideoType(), t?.getType());

            return t && !t.isLocal() && dispatch(trackAdded(t));
        });
    conference.on(
        MHConferenceEvents.TRACK_REMOVED,
        t => t && !t.isLocal() && dispatch(trackRemoved(t)));

    conference.on(
        MHConferenceEvents.TRACK_MUTE_CHANGED,
        (track, participantThatMutedUs) => {
            if (participantThatMutedUs) {
                dispatch(participantMutedUs(participantThatMutedUs, track));
            }
        });

    // Dispatches into features/base/participants follow:
    conference.on(
        MHConferenceEvents.DISPLAY_NAME_CHANGED,
        (id, displayName) => dispatch(participantUpdated({
            conference,
            id,
            name: getNormalizedDisplayName(displayName)
        })));

    conference.on(
        MHConferenceEvents.DOMINANT_SPEAKER_CHANGED,
        id => dispatch(dominantSpeakerChanged(id, conference)));

    conference.on(
        MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
        (participant: Object, json: Object) => dispatch(endpointMessageReceived(participant, json)));

    conference.on(
        MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
        (id, json) => dispatch(nonParticipantMessageReceived(id, json)));

    conference.on(
        MHConferenceEvents.PARTICIPANT_CONN_STATUS_CHANGED,
        (...args) => dispatch(participantConnectionStatusChanged(...args)));

    // Diagnostics only.
    //
    // `userVisibleType: 'restricted-network'` used to be set here, and that is the field the transport
    // middleware keys the recovery profile off: it pinned the session to BOSH, forced iceTransportPolicy
    // to relay and persisted that finding for runtimeForceRelayTtlMs - and it showed the participant a
    // warning. None of it follows from a closed bridge channel. The channel says nothing about the XMPP
    // transport, and while P2P is active it says nothing about media either, because media is not going
    // through the bridge at all. `mediaStatus` is 'unchanged' for the same reason: nothing here
    // establishes that a TURN relay is required. BridgeChannel retries the channel on its own, and a JVB
    // path that is genuinely broken surfaces through ICE failure instead.
    //
    // `signalingStatus` is likewise 'healthy', not 'degraded'. The bridge channel is a media-plane control
    // channel; XMPP is untouched by its loss. Reporting it as degraded put a misleading line in the
    // transport-diagnostics transition feed, which is the page used to confirm exactly this class of bug.
    conference.on(
        MHConferenceEvents.DATA_CHANNEL_CLOSED,
        details => dispatch(transportStateChanged({
            diagnosticsSupported: true,
            mediaStatus: 'unchanged',
            reason: details?.reason || details?.message || 'bridge-data-channel-closed',
            signalingPath: getState()['features/base/config']?.preferBosh ? 'bosh' : 'websocket',
            signalingStatus: 'healthy',
            source: 'data-channel-closed'
        })));

    conference.on(
        MHConnectionQualityEvents.LOCAL_STATS_UPDATED,
        stats => dispatch(transportStateChanged(
            _deriveTransportStateFromStats(
                stats,
                getState()['features/base/conference'].transportState,
                conference))));

    conference.on(
        MHConferenceEvents.USER_JOINED,
        (id, user) => commonUserJoinedHandling({ dispatch }, conference, user));
    conference.on(
        MHConferenceEvents.USER_LEFT,
        (id, user) => commonUserLeftHandling({ dispatch }, conference, user));
    conference.on(
        MHConferenceEvents.USER_ROLE_CHANGED,
        (...args) => dispatch(participantRoleChanged(...args)));
    conference.on(
        MHConferenceEvents.USER_STATUS_CHANGED,
        (...args) => dispatch(participantPresenceChanged(...args)));

    conference.on(
        MHConferenceEvents.BOT_TYPE_CHANGED,
        (id, botType) => dispatch(participantUpdated({
            conference,
            id,
            botType
        })));

    conference.addCommandListener(
        'desktopTrackMuted',
        value => {
            const { id, muted, videoType } = value.attributes;
            const desktopTracks = getState()['features/base/tracks'].filter(
                track => track.videoType === videoType
                    && (track.participantId === id || track.mhTrack?.getSourceName?.() === id)
            );
            const desktopTrack = [ ...desktopTracks ].reverse().find(track => !track.muted)
                || desktopTracks[desktopTracks.length - 1];

            if (desktopTrack && !desktopTrack.local) {
                dispatch(trackMutedChanged(desktopTrack.mhTrack, muted === 'true'));

                if (muted === 'true') {
                    dispatch(require('../../video-layout/actions').toggleTileView(true));
                } else {
                    dispatch(require('../../large-video/actions.any').selectParticipantInLargeVideo(id));

                    if (getState()['features/video-layout'].tileViewEnabled) {
                        dispatch(require('../../video-layout/actions').toggleTileView(false));
                    }
                }
            }
        }
    );

    conference.addCommandListener(
        AVATAR_URL_COMMAND,
        (data, id) => dispatch(participantUpdated({
            conference,
            id,
            avatarURL: data.value
        })));
    conference.addCommandListener(
        EMAIL_COMMAND,
        (data, id) => dispatch(participantUpdated({
            conference,
            id,
            email: data.value
        })));
    conference.addCommandListener(
        DESIGNATION,
        (data, id) => {
            dispatch(participantUpdated({
                conference,
                id,
                designation: data.value
            }));
        }
    );
}

/**
 * Updates the current known state of server-side authentication.
 *
 * @param {boolean} authEnabled - Whether or not server authentication is
 * enabled.
 * @param {string} authLogin - The current name of the logged in user, if any.
 * @returns {{
 *     type: AUTH_STATUS_CHANGED,
 *     authEnabled: boolean,
 *     authLogin: string
 * }}
 */
export function authStatusChanged(authEnabled: boolean, authLogin: string) {
    return {
        type: AUTH_STATUS_CHANGED,
        authEnabled,
        authLogin
    };
}

/**
 * Signals that a specific conference has failed.
 *
 * @param {MHConference} conference - The MHConference that has failed.
 * @param {string} error - The error describing/detailing the cause of the
 * failure.
 * @param {any} params - Rest of the params that we receive together with the event.
 * @returns {{
 *     type: CONFERENCE_FAILED,
 *     conference: MHConference,
 *     error: Error
 * }}
 * @public
 */
export function conferenceFailed(conference: Object, error: string, ...params: any) {
    return {
        type: CONFERENCE_FAILED,
        conference,

        // Make the error resemble an Error instance (to the extent that
        // meet-hour needs it).
        error: {
            name: error,
            params,
            recoverable: undefined
        }
    };
}

/**
 * Signals that a specific conference has been joined.
 *
 * @param {MHConference} conference - The MHConference instance which was
 * joined by the local participant.
 * @returns {{
 *     type: CONFERENCE_JOINED,
 *     conference: MHConference
 * }}
 */
export function conferenceJoined(conference: Object) {
    return {
        type: CONFERENCE_JOINED,
        conference
    };
}

/**
 * Signals that a specific conference has been left.
 *
 * @param {MHConference} conference - The MHConference instance which was
 * left by the local participant.
 * @returns {{
 *     type: CONFERENCE_LEFT,
 *     conference: MHConference
 * }}
 */
export function conferenceLeft(conference: Object) {
    return {
        type: CONFERENCE_LEFT,
        conference
    };
}

/**
 * Signals that the conference subject has been changed.
 *
 * @param {string} subject - The new subject.
 * @returns {{
 *     type: CONFERENCE_SUBJECT_CHANGED,
 *     subject: string
 * }}
 */
export function conferenceSubjectChanged(subject: string) {
    return {
        type: CONFERENCE_SUBJECT_CHANGED,
        subject
    };
}

/**
* Signals that the conference timestamp has been changed.
*
* @param {number} conferenceTimestamp - The UTC timestamp.
* @returns {{
*       type: CONFERENCE_TIMESTAMP_CHANGED,
*       conferenceTimestamp
* }}
*/
export function conferenceTimestampChanged(conferenceTimestamp: number) {
    return {
        type: CONFERENCE_TIMESTAMP_CHANGED,
        conferenceTimestamp
    };
}

/**
 * Changes the visibility of the navigation bar more options.
 *
 * @param {boolean} value - The new value.
 * @returns {Function}
 */
export function toggleNavigationBarMenuOptions(value: boolean) {
    return {
        type: TOGGLE_NAVIGATION_BAR_MORE_OPTIONS,
        value
    };
}

/**
 * Records which of the navigation bar's optional actions did not make it into
 * the bar, so the MenuSideBar can offer exactly those and no others.
 *
 * The navigation bar is the only writer: it is the component that measures the
 * available width, so having it publish the answer is what stops the bar and
 * the menu from showing the same action twice - or neither of them showing it.
 *
 * @param {Array<string>} actions - Ids from `NAV_ACTION_OVERFLOW_ORDER`.
 * @returns {{
 *     type: SET_NAVIGATION_BAR_HIDDEN_ACTIONS,
 *     actions: Array<string>
 * }}
 */
export function setNavigationBarHiddenActions(actions: Array<string>) {
    return {
        type: SET_NAVIGATION_BAR_HIDDEN_ACTIONS,
        actions
    };
}

/**
 *
 */
export function toggleLayoutOverlay(value: boolean) {
    return {
        type: TOGGLE_LAYOUT_OVERLAY,
        value
    };
}

/**
* Signals that the unique identifier for conference has been set.
*
* @param {MHConference} conference - The MHConference instance, where the uuid has been set.
* @returns {{
*   type: CONFERENCE_UNIQUE_ID_SET,
*   conference: MHConference,
* }}
*/
export function conferenceUniqueIdSet(conference: Object) {
    return {
        type: CONFERENCE_UNIQUE_ID_SET,
        conference
    };
}

/**
 * Adds any existing local tracks to a specific conference before the conference
 * is joined. Then signals the intention of the application to have the local
 * participant join the specified conference.
 *
 * @param {MHConference} conference - The {@code MHConference} instance
 * the local participant will (try to) join.
 * @returns {Function}
 */
function _conferenceWillJoin(conference: Object) {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        // Dispose any stale local desktop track BEFORE we walk localTracks below.
        // Native screensharing only mutes the desktop on stop (the sender is kept
        // alive for reuse), so the desktop MHLocalTrack can linger in
        // state['features/base/tracks'] across a screenshare-stop -> hangup ->
        // rejoin cycle. _addLocalTracksToConference hands every local track to
        // MHConference.addTrack in one synchronous pass, each naming its source
        // from _getNextSourceIndex - which reads rtc.localTracks but the new
        // track is only written by _setupNewTrack a microtask later - so two
        // tracks end up sharing one source name and the bridge publishes the
        // camera stream under the desktop's name (the remote participant sees
        // the camera on the screenshare tile). destroyLocalDesktopTrackIfExists
        // removes the stale desktop from the store and disposes it; do that
        // first, then read localTracks, so the synchronous pass only sees the
        // camera. Mirrors the React Native moveToRoom fix in commit 6360a92de1.
        await dispatch(destroyLocalDesktopTrackIfExists());

        const localTracks
            = getLocalTracks(getState()['features/base/tracks'])
                .map(t => t.mhTrack);

        if (localTracks.length && !require('../../visitors/functions').iAmVisitor(getState())) {
            _addLocalTracksToConference(conference, localTracks);
        }

        dispatch(conferenceWillJoin(conference));
    };
}

/**
 * Signals the intention of the application to have a conference initialized.
 *
 * @returns {{
*     type: CONFERENCE_WILL_INIT
* }}
*/
export function conferenceWillInit() {
    return {
        type: CONFERENCE_WILL_INIT
    };
}

/**
 * Signals the intention of the application to have the local participant
 * join the specified conference.
 *
 * @param {MHConference} conference - The {@code MHConference} instance
 * the local participant will (try to) join.
 * @returns {{
 *     type: CONFERENCE_WILL_JOIN,
 *     conference: MHConference
 * }}
 */
export function conferenceWillJoin(conference: Object) {
    return {
        type: CONFERENCE_WILL_JOIN,
        conference
    };
}

/**
 * Signals the intention of the application to have the local participant leave
 * a specific conference. Similar in fashion to CONFERENCE_LEFT. Contrary to it
 * though, it's not guaranteed because CONFERENCE_LEFT may be triggered by
 * lib-meet-hour and not the application.
 *
 * @param {MHConference} conference - The MHConference instance which will
 * be left by the local participant.
 * @returns {{
 *     type: CONFERENCE_LEFT,
 *     conference: MHConference,
 *     isRedirect: boolean
 * }}
 */
export function conferenceWillLeave(conference: Object, isRedirect?: boolean) {
    return {
        type: CONFERENCE_WILL_LEAVE,
        conference,
        isRedirect
    };
}

/**
 * Ends the meeting for everybody, server side.
 *
 * Asks the end_conference component to destroy the room. Prosody re-checks that
 * the sender really is a moderator occupant and then destroys it, which removes
 * every occupant at once and cascades: breakout rooms are destroyed with the
 * main room, the visitor nodes are disconnected so viewers stop watching a dead
 * meeting, and the backstage room goes with it.
 *
 * The alternative -- kicking each participant from the client -- cannot reach
 * any of those, is O(participants) round trips, and silently leaves people
 * behind if the moderator's tab closes half way through. The local participant
 * is destroyed along with everyone else, so there is deliberately no local
 * hangup here.
 *
 * @returns {Function} Resolves true when the request was sent, false when the
 * deployment has no end_conference component and the caller must fall back.
 */
export function endConference() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState());

        if (!conference) {
            return false;
        }

        if (!conference.isEndConferenceSupported?.()) {
            logger.warn('Cannot end the conference for everyone: no end_conference component.');

            return false;
        }

        conference.end();

        return true;
    };
}

/**
 * Initializes a new conference.
 *
 * @returns {Function}
 */
export function createConference(overrideRoom?: string) {
    return (dispatch: Function, getState: Function) => {
        const state = getState();
        const { password, room, conference: existingConference } = state['features/base/conference'];

        // If we already have a conference instance, don't create a new one.
        // This can happen during connection resume after internet disconnect/reconnect.
        if (existingConference) {
            logger.info('createConference: Conference already exists, skipping creation. The existing conference will reconnect when the XMPP connection resumes.');

            return;
        }

        try {
            const { connection, locationURL } = state['features/base/connection'];

            if (!connection) {
                throw new Error('Cannot create a conference without a connection!');
            }

            if (!room) {
                throw new Error('Cannot join a conference without a room name!');
            }

            let tmp: any = overrideRoom || room;

            // A token moderator who chose "start in backstage" on the prejoin
            // joins the backstage room directly (on web the prejoin middleware
            // does this before conference.js connects). Consumed here so a
            // later Go Live does not bounce them back.
            if (!overrideRoom && shouldDirectJoinBackstage(state)) {
                tmp = `${room}${BACKSTAGE_ROOM_SUFFIX}`;
                dispatch({
                    type: SET_START_BACKSTAGE,
                    enabled: false
                });
            }

            let _room: any = getBackendSafeRoomName(tmp);

            if (tmp.domain) {

                _room = new String(tmp); // eslint-disable-line no-new-wrappers
                _room.domain = tmp.domain;
            }

            const options = getConferenceOptions(state, dispatch);

            const conference = connection.initMHConference(_room, options);

            connection[MH_CONNECTION_CONFERENCE_KEY] = conference;

            conference[MH_CONFERENCE_URL_KEY] = locationURL;

            dispatch(_conferenceWillJoin(conference));

            _addConferenceListeners(conference, dispatch, state, getState);

            sendLocalParticipant(state, conference);

            // "Join hidden" rides the join presence as a participant property
            // (it is chosen before the occupant exists, so it cannot be an
            // IQ). Same as conference.js does on web: set before join, or the
            // moderator is visible to the audience for a round-trip.
            const hiddenAudience = getJoinHiddenAudience(state);

            if (hiddenAudience) {
                conference.setLocalParticipantProperty(WEBINAR_HIDDEN_REQUEST_PROPERTY, hiddenAudience);
            }

            conference.join(password);

        } catch (error) {
            logger.error('createConference failed:', error);
        }
    };
}

/**
 * Will try to join the conference again in case it failed earlier with
 * {@link MHConferenceErrors.AUTHENTICATION_REQUIRED}. It means that Jicofo
 * did not allow to create new room from anonymous domain, but it can be tried
 * again later in case authenticated user created it in the meantime.
 *
 * @returns {Function}
 */
export function checkIfCanJoin() {
    return (dispatch: Function, getState: Function) => {
        const { authRequired, password }
            = getState()['features/base/conference'];

        authRequired && dispatch(_conferenceWillJoin(authRequired));
        authRequired && authRequired.join(password);
    };
}

/**
 * Signals the data channel with the bridge has successfully opened.
 *
 * @returns {{
 *     type: DATA_CHANNEL_OPENED
 * }}
 */
export function dataChannelOpened() {
    return {
        type: DATA_CHANNEL_OPENED
    };
}

/**
 * Builds normalized live transport state from connection-quality stats.
 *
 * @param {Object} stats - Local connection-quality stats.
 * @param {Object} previousTransportState - Previously stored transport state.
 * @param {Object} conference - Active conference instance.
 * @returns {Object}
 */
function _deriveTransportStateFromStats(stats = {}, previousTransportState = {}, conference) {
    const transportEntries = Array.isArray(stats.transport) ? stats.transport : [];
    const activeTransport = transportEntries.length ? transportEntries[transportEntries.length - 1] : undefined;
    const isRelay = Boolean(activeTransport
        && (activeTransport.localCandidateType === 'relay'
            || activeTransport.remoteCandidateType === 'relay'));
    const signalingPath = _getCurrentSignalingPath(conference, previousTransportState);
    const activeTransportLabel = activeTransport
        ? _describeActiveTransport(activeTransport)
        : previousTransportState.activeTransportLabel;
    const preserveWebSocketFallback = signalingPath === 'bosh'
        && previousTransportState.userVisibleType === 'websocket-fallback';
    const preserveRestrictedNetwork = isRelay
        && previousTransportState.userVisibleType === 'restricted-network';
    const preservedUserVisibleType = preserveWebSocketFallback
        ? 'websocket-fallback'
        : preserveRestrictedNetwork
            ? 'restricted-network'
            : undefined;

    return {
        activeTransport,
        activeTransportLabel,
        diagnosticsSupported: true,
        forceTurnRelay: Boolean(previousTransportState.forceTurnRelay || isRelay),
        mediaStatus: activeTransport
            ? isRelay ? 'relay-active' : 'direct-active'
            : previousTransportState.mediaStatus || 'unknown',
        networkType: activeTransport?.networkType,
        p2pActive: Boolean(activeTransport?.p2p),
        reason: preservedUserVisibleType
            ? previousTransportState.reason
            : activeTransportLabel || previousTransportState.reason,
        relayProtocol: isRelay
            ? _normalizeRelayProtocol(activeTransport.relayProtocol || activeTransport.type)
            : undefined,
        signalingPath,
        signalingStatus: signalingPath === 'bosh'
            ? preserveWebSocketFallback ? 'fallback-active' : previousTransportState.signalingStatus || 'healthy'
            : 'healthy',
        source: 'connection-quality-stats',
        transportEntries,
        transportRtt: activeTransport?.rtt,
        userVisibleSeverity: preservedUserVisibleType
            ? previousTransportState.userVisibleSeverity || 'warn'
            : undefined,
        userVisibleType: preservedUserVisibleType,
        websocketStatus: signalingPath === 'websocket'
            ? 'healthy'
            : preserveWebSocketFallback
                ? previousTransportState.websocketStatus || 'failed'
                : 'bypassed'
    };
}

/**
 * Returns a normalized summary for the active transport path.
 *
 * @param {Object} activeTransport - Currently selected ICE transport.
 * @returns {string}
 */
function _describeActiveTransport(activeTransport = {}) {
    const isRelay = activeTransport.localCandidateType === 'relay'
        || activeTransport.remoteCandidateType === 'relay';
    const relayProtocol = _normalizeRelayProtocol(activeTransport.relayProtocol || activeTransport.type);
    const protocolLabel = relayProtocol === 'tls'
        ? 'TLS 443'
        : relayProtocol.toUpperCase();
    const pathLabel = isRelay
        ? 'TURN relay'
        : activeTransport.p2p
            ? 'P2P direct'
            : 'JVB direct';

    return `${pathLabel} over ${protocolLabel}`;
}

/**
 * Gets the current signaling path by checking the underlying XMPP transport.
 *
 * @param {Object} conference - Active conference instance.
 * @param {Object} previousTransportState - Previously stored transport state.
 * @returns {string}
 */
function _getCurrentSignalingPath(conference, previousTransportState = {}) {
    const websocketCandidates = [
        conference?.xmpp?.connection?.isUsingWebSocket,
        conference?.connection?.xmpp?.connection?.isUsingWebSocket,
        conference?.room?.xmpp?.connection?.isUsingWebSocket
    ];
    const websocketIndicator = websocketCandidates.find(value => typeof value === 'boolean');

    if (websocketIndicator === true) {
        return 'websocket';
    }

    if (websocketIndicator === false) {
        return previousTransportState.userVisibleType === 'websocket-fallback'
            || previousTransportState.signalingStatus === 'fallback-active'
            ? 'bosh'
            : previousTransportState.signalingPath || 'bosh';
    }

    return previousTransportState.signalingPath || 'unknown';
}

/**
 * Normalizes relay protocol values for UI rendering.
 *
 * @param {string} relayProtocol - Raw relay protocol.
 * @returns {string}
 */
function _normalizeRelayProtocol(relayProtocol) {
    const value = String(relayProtocol || '').toLowerCase();

    if (value === 'tls' || value === 'tcp' || value === 'udp') {
        return value;
    }

    if (value === 'ssltcp') {
        return 'tls';
    }

    return value || 'unknown';
}

/**
 * Stores the current conference transport status so all platforms can react
 * consistently to websocket, bridge-channel and relay fallback changes.
 *
 * @param {Object} transportState - Current normalized transport state.
 * @returns {{
 *     type: TRANSPORT_STATE_CHANGED,
 *     transportState: Object
 * }}
 */
export function transportStateChanged(transportState: Object = {}) {
    return {
        type: TRANSPORT_STATE_CHANGED,
        transportState: {
            ...transportState,
            updatedAt: Date.now()
        }
    };
}

/**
 * Signals that a participant sent an endpoint message on the data channel.
 *
 * @param {Object} participant - The participant details sending the message.
 * @param {Object} data - The data carried by the endpoint message.
 * @returns {{
*      type: ENDPOINT_MESSAGE_RECEIVED,
*      participant: Object,
*      data: Object
* }}
*/
export function endpointMessageReceived(participant: Object, data: Object) {
    return {
        type: ENDPOINT_MESSAGE_RECEIVED,
        participant,
        data
    };
}

/**
 * Signals that we've been kicked out of the conference.
 *
 * @param {MHConference} conference - The {@link MHConference} instance
 * for which the event is being signaled.
 * @param {MHParticipant} participant - The {@link MHParticipant}
 * instance which initiated the kick event.
 * @param {string | undefined} reason - Reason Given.
 *
 * @returns {{
 *     type: KICKED_OUT,
 *     conference: MHConference,
 *     participant: MHParticipant
 * }}
 */
export function kickedOut(conference: Object, participant: Object, reason: undefined | string) {
    return {
        type: KICKED_OUT,
        conference,
        participant,
        reason
    };
}

/**
 * Signals that the lock state of a specific MHConference changed.
 *
 * @param {MHConference} conference - The MHConference which had its lock
 * state changed.
 * @param {boolean} locked - If the specified conference became locked, true;
 * otherwise, false.
 * @returns {{
 *     type: LOCK_STATE_CHANGED,
 *     conference: MHConference,
 *     locked: boolean
 * }}
 */
export function lockStateChanged(conference: Object, locked: boolean) {
    return {
        type: LOCK_STATE_CHANGED,
        conference,
        locked
    };
}

/**
 * Signals that a non participant endpoint message has been received.
 *
 * @param {string} id - The resource id of the sender.
 * @param {Object} json - The json carried by the endpoint message.
 * @returns {{
*      type: NON_PARTICIPANT_MESSAGE_RECEIVED,
*      id: Object,
*      json: Object
* }}
*/
export function nonParticipantMessageReceived(id: string, json: Object) {
    return {
        type: NON_PARTICIPANT_MESSAGE_RECEIVED,
        id,
        json
    };
}

/**
 * Updates the known state of start muted policies.
 *
 * @param {boolean} audioMuted - Whether or not members will join the conference
 * as audio muted.
 * @param {boolean} videoMuted - Whether or not members will join the conference
 * as video muted.
 * @returns {{
 *     type: SET_START_MUTED_POLICY,
 *     startAudioMutedPolicy: boolean,
 *     startVideoMutedPolicy: boolean
 * }}
 */
export function onStartMutedPolicyChanged(
        audioMuted: boolean, videoMuted: boolean) {
    return {
        type: SET_START_MUTED_POLICY,
        startAudioMutedPolicy: audioMuted,
        startVideoMutedPolicy: videoMuted
    };
}

/**
 * Sets whether or not peer2peer is currently enabled.
 *
 * @param {boolean} p2p - Whether or not peer2peer is currently active.
 * @returns {{
 *     type: P2P_STATUS_CHANGED,
 *     p2p: boolean
 * }}
 */
export function p2pStatusChanged(p2p: boolean) {
    return {
        type: P2P_STATUS_CHANGED,
        p2p
    };
}

/**
 * Signals to play touch tones.
 *
 * @param {string} tones - The tones to play.
 * @param {number} [duration] - How long to play each tone.
 * @param {number} [pause] - How long to pause between each tone.
 * @returns {{
 *     type: SEND_TONES,
 *     tones: string,
 *     duration: number,
 *     pause: number
 * }}
 */
export function sendTones(tones: string, duration: number, pause: number) {
    return {
        type: SEND_TONES,
        tones,
        duration,
        pause
    };
}

/**
 * Enables or disables the Follow Me feature.
 *
 * @param {boolean} enabled - Whether or not Follow Me should be enabled.
 * @returns {{
 *     type: SET_FOLLOW_ME,
 *     enabled: boolean
 * }}
 */
export function setFollowMe(enabled: boolean) {
    return {
        type: SET_FOLLOW_ME,
        enabled
    };
}

/**
 * Enables or disables the Mute reaction sounds feature.
 *
 * @param {boolean} muted - Whether or not reaction sounds should be muted for all participants.
 * @param {boolean} updateBackend - Whether or not the moderator should notify all participants for the new setting.
 * @returns {{
*     type: SET_START_REACTIONS_MUTED,
*     muted: boolean
* }}
*/
export function setStartReactionsMuted(muted: boolean, updateBackend = false) {
    return {
        type: SET_START_REACTIONS_MUTED,
        muted,
        updateBackend
    };
}

/**
 * Sets the password to join or lock a specific MHConference.
 *
 * @param {MHConference} conference - The MHConference which requires a
 * password to join or is to be locked with the specified password.
 * @param {Function} method - The MHConference method of password protection
 * such as join or lock.
 * @param {string} password - The password with which the specified conference
 * is to be joined or locked.
 * @returns {Function}
 */
export function setPassword(
        conference: Object,
        method: Function,
        password: string) {
    return (dispatch: Dispatch<any>, getState: Function): ?Promise<void> => {
        switch (method) {
        case conference.join: {
            let state = getState()['features/base/conference'];

            // const preregData = getState()['features/base/mtApi']?.pregData ?? {};

            dispatch({
                type: SET_PASSWORD,
                conference,
                method,
                password
            });

            // Shoeb - Commenting this to have Pcode persist in Localstorage (Jan 10, 2024)

            // if (preregData.hasOwnProperty('passcode')) {
            //     // delete preregData.passcode;
            //     dispatch({
            //         type: PRE_REG_API,
            //         preRegData: preregData
            //     });
            // }

            // dispatch(setAutoPassword(null));

            // Join the conference with the newly-set password.

            // Make sure that the action did set the password.
            state = getState()['features/base/conference'];
            if (state.password === password

                    // Make sure that the application still wants the
                    // conference joined.
                    && !state.conference) {
                method.call(conference, password);
            }

            break;
        }

        case conference.lock: {
            const state = getState()['features/base/conference'];

            if (state.conference === conference) {
                return (
                    method.call(conference, password)
                            .then(() => dispatch({
                                type: SET_PASSWORD,
                                conference,
                                method,
                                password
                            }))
                            .catch(error => dispatch({
                                type: SET_PASSWORD_FAILED,
                                error
                            }))
                );
            }

            return Promise.reject();
        }
        }
    };
}

/**
 * Sets (the name of) the room of the conference to be joined.
 *
 * @param {(string|undefined)} room - The name of the room of the conference to
 * be joined.
 * @returns {{
 *     type: SET_ROOM,
 *     room: string
 * }}
 */
export function setRoom(room: ?string) {
    return {
        type: SET_ROOM,
        room
    };
}

/**
 * Sets whether or not members should join audio and/or video muted.
 *
 * @param {boolean} startAudioMuted - Whether or not members will join the
 * conference as audio muted.
 * @param {boolean} startVideoMuted - Whether or not members will join the
 * conference as video muted.
 * @returns {Function}
 */
export function setStartMutedPolicy(
        startAudioMuted: boolean, startVideoMuted: boolean) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState());

        conference && conference.setStartMutedPolicy({
            audio: startAudioMuted,
            video: startVideoMuted
        });

        return dispatch(
            onStartMutedPolicyChanged(startAudioMuted, startVideoMuted));
    };
}

/**
 * Changing conference subject.
 *
 * @param {string} subject - The new subject.
 * @returns {void}
 */
export function setSubject(subject: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const { conference } = getState()['features/base/conference'];

        if (conference) {
            conference.setSubject(subject || '');
        } else {
            dispatch({
                type: SET_PENDING_SUBJECT_CHANGE,
                subject
            });
        }
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {boolean} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function forcemuteRemoteParticipant(id: any, mediaType: any, bool: ?boolean, isMuteAll?: boolean) {

    return {
        type: FORCE_MUTE_REMOTE_PARTICIPANT,
        id,
        mediaType,
        bool,
        isMuteAll
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {Object} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function moderatorForceMuted(mediaType: any, bool: ?Object, isMuteAll?: boolean, moderatorId?: string) {
    return {
        type: FORCE_MUTE_REMOTE_PARTICIPANT_ID,
        mediaType,
        bool,
        isMuteAll,
        moderatorId
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {boolean} isMuteAll - The global mute all state.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function moderatorForceMutedUsAll(mediaType: any, isMuteAll: boolean) {

    return {
        type: MODERATOR_MUTED_US_ALL,
        mediaType,
        isMuteAll
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {boolean} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function forcemuteRemoteParticipantVideo(id: any, mediaType: any, bool: ?boolean) {

    return {
        type: FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
        id,
        mediaType,
        bool
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {boolean} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function addforcemuteRemoteParticipant(id: any, mediaType: any, bool: ?boolean) {

    return {
        type: ADD_FORCE_MUTE_REMOTE_PARTICIPANT,
        id,
        mediaType,
        bool
    };
}

/**
 * Receives an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {?string} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function getforcemuteRemoteParticipant(id: any, mediaType: any, bool: ?string) {

    return {
        type: GET_FORCE_MUTE_REMOTE_PARTICIPANT,
        id,
        mediaType,
        bool
    };
}

/**
 * Receives an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @param {?string} bool - The boolean for force locking a audio.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function getforcemuteRemoteParticipantVideo(id: any, mediaType: any, bool: ?string) {

    return {
        type: GET_FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
        id,
        mediaType,
        bool
    };
}

/**
 * Loading Checker for User.
 *
 * @param {?boolean} bool - COntrols Loading from the redux.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     bool: boolean
 * }}
 */
export function isLoadingChecker(bool: boolean, connecting: boolean) {
    return {
        type: LOADING_CHECKER,
        isPrejoinLoading: bool,
        isConnecting: connecting
    };
}

/**
 * Loading Checker for alerts.
 *
 * @param {?boolean} bool - The boolean for controling alerts.
 * @returns {{
 *     type: OPEN_NOTIFICATION,
 *     bool: boolean
 * }}
 */
export function openNotification(bool: boolean) {
    return {
        type: OPEN_NOTIFICATION,
        notication: bool
    };
}

/**
 * Object data for alerts.
 *
 * @param {?Object} notifier - The object for controling alerts.
 * @returns {{
 *     type: NOTIFICATION_DETAILS,
 *     bool: boolean
 * }}
 */
export function notificationDetails(notifier: ?Object) {
    return {
        type: NOTIFICATION_DETAILS,
        notifierDetails: notifier
    };
}

/**
 * Object data for alerts.
 *
 * @param {?boolean} prejoin - The object for controling alerts.
 * @returns {{
 *     type: NOTIFICATION_DETAILS,
 *     prejoin: boolean
 * }}
 */
export function prejoinMobileDisabled(prejoin: ?boolean) {
    return {
        type: PREJOIN_MOBILE_DISABLED,
        prejoin
    };
}

/**
 * Object data for conference time.
 *
 * @param {?string} timer - The object for controling alerts.
 * @returns {{
 *     type: NOTIFICATION_DETAILS,
 *     prejoin: string
 * }}
 */
export function setConferenceTime(timer: ?string) {
    return {
        type: CONFERENCE_TIME,
        timer
    };
}

/**
 * Object data for alerts.
 *
 * @param {?string} startTime - The object for controling alerts.
 * @returns {{
 *     type: NOTIFICATION_DETAILS,
 *     prejoin: string
 * }}
 */
export function setTimeOfUser(startTime: ?string) {
    return {
        type: USER_JOINED_TIME,
        startTime
    };

}

/**
 * Signals that a specific conference join is in progress.
 *
 * @param {MHConference} conference - The MHConference instance for which join by the local participant
 * is in progress.
 * @returns {{
*     type: CONFERENCE_JOIN_IN_PROGRESS,
*     conference: MHConference
* }}
*/
export function conferenceJoinInProgress(conference: Object) {
    return {
        type: CONFERENCE_JOIN_IN_PROGRESS,
        conference
    };
}

/**
 * Signals that a specific conference call is ended.
 *
 * @param {boolean} conferenceEnded - The boolean for controling alerts.
 * @param {string} [conferenceEndedMessage] - The i18n key explaining why, shown
 * on the call-ended screen. Defaults to the "closing all terminals" wording,
 * which fits the moderator who is doing the ending; someone whose meeting was
 * ended for them needs to be told that instead.
 * @returns {{
 *     type: CALL_ENDED,
 *     conferenceEnded: boolean,
 *     conferenceEndedMessage: string | undefined
 * }}
 */
export function updateConferenceCallEnded(conferenceEnded: boolean, conferenceEndedMessage?: string) {
    return {
        type: CALL_ENDED,
        conferenceEnded,
        conferenceEndedMessage
    };
}

/**
 * Sets the current connection indicator status.
 *
 * @param {string} status - The current connection indicator status.
 * @returns {{
 *     type: CONNECTION_INDICATOR_STATUS,
 *     status: string
 * }}
 */
export function connectionIndicatorStatus(status: string) {
    return {
        type: CONNECTION_INDICATOR_STATUS,
        status
    };
}

/**
 * Creates an action to temporarily reduce the volume of all users.
 * Used during voice command processing to make user voices quieter.
 *
 * @returns {Object} An action object with type 'REDUCE_ALL_USER_VOLUME_TEMPORARILY'.
 */
export function reduceAllUserVolumeTemporarily() {
    return {
        type: REDUCE_ALL_USER_VOLUME_TEMPORARILY
    };
}

/**
 * ModeratorUnlockVideo
 * @returns {Object} An action object with type 'MODERATOR_UNLOCK_VIDEO'.
 */
export function moderatorunclockVideo() {
    return {
        type: MODERATOR_UNLOCK_VIDEO
    };
}

/**
 * ModeratorUnlockAudio
 * @returns {Object} An action object with type 'MODERATOR_UNLOCK_AUDIO'.
 */
export function moderatorunclockAudio() {
    return {
        type: MODERATOR_UNLOCK_AUDIO
    };
}

/**
 * conference expired state
 * @returns {Object} An action object with type 'CONFERENCE_EXPIRED'.
 */
export function setConferenceExpired() {
    return {
        type: CONFERENCE_EXPIRED
    };
}

/**
 * Redirects to a new visitor node.
 *
 * @param {string | undefined} vnode - The vnode to use or undefined if moving back to the main room.
 * @param {string} focusJid - The focus jid to use.
 * @param {string} username - The username to use.
 * @param {string} [role] - The main-meeting role granted with a promotion.
 * @returns {void}
 */
export function redirect(vnode, focusJid, username, role) {
    return (dispatch, getState) => {
        const grantedRole = normalizePromotionRole(role);

        vlog('6. redirect() received', { vnode,
            focusJid,
            username,
            role: grantedRole,
            isPromotionBackToMain: !vnode });

        // Coming back to the meeting. Put the loading screen up before the
        // disconnect so the call never sits on a half-left room. A move onto
        // a viewer node already raised its own screen (kind 'to-viewer').
        if (!vnode) {
            const current = getState()['features/visitors'].roleTransition;

            dispatch(setRoleTransition({
                kind: 'to-meeting',
                role: grantedRole || current?.role
            }));
        }

        const newConfig = getVisitorOptions(getState, vnode, focusJid, username);

        if (!newConfig) {
            verr('6x. redirect aborted: getVisitorOptions returned no config (missing params)', { vnode,
                focusJid });
            logger.warn('Not redirected missing params');
            dispatch(abortRoleTransition());

            return;
        }

        vlog('6a. redirect: overwriteConfig with visitor options', newConfig);
        dispatch(overwriteConfig(newConfig)) // @ts-ignore
            .then(() => {
                vlog('6b. redirect: disconnectAny(true) (leave current room)');

                return dispatch(disconnectAny(true));
            })
            .then(() => {
                vlog('6c. redirect: setIAmVisitor', { iAmVisitor: Boolean(vnode) });

                return dispatch(setIAmVisitor(Boolean(vnode)));
            })
            .then(() => {
                if (!vnode) {
                    // We are promoting back to the main room. Clear the visitor preference before
                    // connecting, otherwise the main-room conference request can still ask Jicofo for
                    // a visitor redirect instead of using the authorized customusername.
                    dispatch(setPreferVisitor(false));
                }
            })

            // we do not clear local tracks on error, so we need to manually clear them
            .then(() => dispatch(destroyLocalTracks()))
            .then(() => dispatch(conferenceWillInit()))
            .then(() => {
                vlog('6d. redirect: connectVisitor() (join visitor node)');

                // NOTE: Imported lazily (instead of a static top-level import) to avoid a circular
                // module dependency with connection/actions.web -> connection/actions.any ->
                // conference/actions, which crashes React Fast Refresh's eager export enumeration.
                return import('../connection/actions.web').then(({ connectVisitor }) => dispatch(connectVisitor()));
            })
            .then(con => {
                vlog('6e. redirect: connectVisitor resolved; starting conference (receive-only)', { hasConnection: Boolean(con) });

                // Clear the gum pending state in case we have set it to pending since we are starting the
                // conference without tracks.
                dispatch(gumPending([ MEDIA_TYPE.AUDIO, MEDIA_TYPE.VIDEO ], IGUMPendingState.NONE));

                if (!vnode) {
                    const state = getState();
                    const { enableMediaOnPromote = {} } = state['features/base/config'].visitors ?? {};
                    const { audio = false, video = false } = enableMediaOnPromote;

                    if (audio) {
                        const { available, muted, unmuteBlocked } = state['features/base/media'].audio;
                        const { startSilent } = state['features/base/config'];

                        // do not unmute the user if he was muted before (on the prejoin, the config
                        // or URL param, etc.)
                        if (!unmuteBlocked && !muted && !startSilent && available) {
                            dispatch(setAudioMuted(false, true));

                            // // FIXME: The old conference logic still relies on this event being emitted.
                            typeof APP === 'undefined' || APP.conference.muteAudio(false);
                        }
                    }

                    if (video) {
                        const { muted, unmuteBlocked } = state['features/base/media'].video;

                        // do not unmute the user if he was muted before (on the prejoin, the config, URL param or
                        // audo only, etc)
                        if (!unmuteBlocked && !muted && hasAvailableDevices(state, 'videoInput')) {
                            dispatch(setVideoMuted(false, VIDEO_MUTISM_AUTHORITY.USER, true));

                            // // FIXME: The old conference logic still relies on this event being emitted.
                            typeof APP === 'undefined' || APP.conference.muteVideo(false, false);
                        }
                    }
                }

                // FIXME: Workaround for the web version. To be removed once we get rid of conference.js
                // Meet Hour's startConference signature is (con, tracks); pass the connection that
                // connectVisitor() resolved with so the (visitor or main) room is created against it.
                if (typeof APP !== 'undefined') {
                    vlog('6f. redirect: APP.conference.startConference(con, [])');
                    APP.conference.startConference(con, []);
                }
            })
            .catch(e => {
                verr('redirect chain failed', e);
                dispatch(abortRoleTransition());
            });
    };
}

/**
 * Sets the custom participant visibility.
 * @param {boolean} bool - The boolean for custom participant visibility.
 * @returns
 */
export function setCustomParticipantVisible(bool: boolean) {
    return {
        type: IS_CUSTOM_PARTICIPANT_VISIBLE,
        bool
    };
}

/**
 * Sets the custom participant visibility for everyone.
 * @param {boolean} bool - The boolean for custom participant visibility for everyone.
 * @returns
 */
export function setCustomParticipantEveryone(bool: boolean) {
    return {
        type: IS_CUSTOM_PARTICIPANT_EVERYONE,
        bool
    };
}

/**
 * Sets the object fit value for large video.
 * @param {*} value - The object fit value.
 * @returns {{
 *     type: SET_PARTICIPANT_OBJECT_FIT,
 *     id: string,
 *     objectFit: string
 * }}
 */
export function setObjectFit(value: string) {
    return {
        type: SET_OBJECT_FIT,
        value
    };
}
